import { DEFAULT_QUIZ_RULES_HTML, emptyQuizDraft } from "./quiz/quizDraftDefaults";

/** Microlab brand accent used by the persistent AI assistant. */
export const AI_ASSISTANT_BLUE = "#0061FE";

/** Desktop panel width as a fraction of the viewport. */
export const AI_PANEL_WIDTH_RATIO = 0.33;

/** Approximate panel width used for layout margin (matches CSS). */
export const AI_PANEL_WIDTH_CSS = "min(33vw, 480px)";

/** sessionStorage key prefix — always suffixed with authenticated user id. */
export const AI_STORAGE_PREFIX = "microlab_ai_assistant_v1";

/** Max messages in one AI chat before the user must start a new conversation. */
export const MAX_CONVERSATION_MESSAGES = 20;

export const CONVERSATION_LIMIT_MESSAGE =
  "This conversation has reached the 20-message limit. Start a new conversation to continue.";

export const MESSAGE_STATUS = {
  PENDING: "pending",
  SENT: "sent",
  FAILED: "failed",
  STREAMING: "streaming",
};

export const RESULTS_OPTION_CHIPS = [
  { id: "open-results", label: "Open Results page", prompt: "__open_results__" },
  {
    id: "retry-results",
    label: "Refresh analysis",
    prompt: "Analyze my student quiz results again",
  },
  {
    id: "list-students",
    label: "List students",
    prompt: "Show students in my authorized scope",
  },
];

export const USERS_OPTION_CHIPS = [
  {
    id: "list-students",
    label: "List students",
    prompt: "Show students in my authorized scope",
  },
  {
    id: "show-marks",
    label: "Show marks",
    prompt: "Show student marks and quiz scores",
  },
  { id: "open-results", label: "Open Results page", prompt: "__open_results__" },
];

/** Build a chat reply from a university/class user list (offline directory path). */
export function formatUsersDirectory(users, { universityWide = false } = {}) {
  const list = Array.isArray(users) ? users : [];
  if (!list.length) {
    return {
      reply: universityWide
        ? "There are no matching users in your university yet."
        : "There are no students linked to your authorized classes yet. Once students are assigned or submit a quiz, I can list their details here.",
      options: USERS_OPTION_CHIPS,
    };
  }
  const lines = list
    .slice(0, 25)
    .map((u) => {
      const name =
        [u.first_name, u.last_name].filter(Boolean).join(" ") ||
        u.name ||
        "—";
      return `- ${name} · ${u.email || "—"} · ${u.role || "student"}`;
    })
    .join("\n");
  return {
    reply: [
      universityWide
        ? `Here are users in your university (${list.length}).`
        : `Here are students from your authorized scope (${list.length}).`,
      lines,
      list.length > 25 ? `(Showing 25 of ${list.length}.)` : null,
    ]
      .filter(Boolean)
      .join("\n"),
    options: USERS_OPTION_CHIPS,
  };
}

/** Build a chat reply from GET /quiz/results (offline / direct analysis path). */
export function formatQuizResultsAnalysis(payload) {
  const body = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const results = Array.isArray(body?.results) ? body.results : [];
  const total = Number(body?.total ?? results.length) || 0;

  if (!total || results.length === 0) {
    return {
      reply:
        "There are no submitted quiz attempts for your authorized classes yet. Once students complete a quiz, I can summarize scores, averages, and who needs attention.",
      options: RESULTS_OPTION_CHIPS,
    };
  }

  const scores = results
    .map((r) => Number(r.score))
    .filter((n) => Number.isFinite(n));
  const avg =
    scores.length > 0
      ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
      : null;
  const sorted = [...results].sort(
    (a, b) => (Number(b.score) || 0) - (Number(a.score) || 0)
  );
  const top = sorted[0];
  const low = sorted[sorted.length - 1];
  const lines = results
    .slice(0, 12)
    .map((r) => {
      const when = r.submitted_at
        ? new Date(r.submitted_at).toLocaleString()
        : "";
      return `- ${r.student_name || "Student"} · ${r.quiz_title || "Quiz"} · score ${
        r.score ?? "—"
      }${r.correct_count != null && r.total_questions != null ? ` (${r.correct_count}/${r.total_questions})` : ""}${
        when ? ` · ${when}` : ""
      }`;
    })
    .join("\n");

  return {
    reply: [
      `Here’s an analysis of ${total} submitted attempt${total === 1 ? "" : "s"} in your authorized classes.`,
      avg != null ? `Average score: ${avg}.` : null,
      top && low && top !== low
        ? `Highest: ${top.student_name || "Student"} (${top.score ?? "—"}) · Lowest: ${
            low.student_name || "Student"
          } (${low.score ?? "—"}).`
        : null,
      lines,
      total > 12
        ? `(Showing 12 of ${total}. Open the Results page for the full list and filters.)`
        : null,
    ]
      .filter(Boolean)
      .join("\n"),
    options: RESULTS_OPTION_CHIPS,
  };
}

/** Starter titles shown in a blank chat. */
export const STARTER_ACTIONS = [
  {
    id: "create-quiz",
    label: "Create a quiz",
    description: "Build a quiz from Microlab or your private bank",
    prompt: "Create a quiz",
    comingSoon: false,
  },
  {
    id: "check-quiz",
    label: "Check quiz results",
    description: "See scores from your assigned classes",
    prompt: "Show the results of students in my classes",
    comingSoon: false,
  },
  {
    id: "list-users",
    label: "List students / users",
    description: "User details in your authorized scope",
    prompt: "Show students in my authorized scope",
    comingSoon: false,
  },
  {
    id: "ask-quizzes",
    label: "Ask about my quizzes",
    description: "Questions about titles, banks, and setup",
    prompt: "Help me with my quizzes and question bank",
    comingSoon: false,
  },
  {
    id: "make-presentation",
    label: "Make a presentation",
    description: "Draft slides with AI, then confirm",
    prompt: "Make a presentation about connective tissues with 6 slides",
    comingSoon: false,
  },
];

export const QUICK_ACTIONS = STARTER_ACTIONS.filter((a) => !a.comingSoon).map((a) => ({
  id: a.id,
  label: a.label,
  prompt: a.prompt,
}));

export const QUIZ_QUICK_ACTIONS = [
  {
    id: "create-quiz",
    label: "Create a quiz",
    prompt: "Create a quiz",
  },
  {
    id: "histology-quiz",
    label: "Histology · 20 medium · 30 min",
    prompt:
      "Create a histology quiz using 20 questions from Microlab's public bank, with a 30-minute time limit.",
  },
  {
    id: "check-quiz",
    label: "Check quiz results",
    prompt: "Show the results of students in my classes",
  },
];

/**
 * Short follow-up after topic pick: "3 question 10 min", "10 questions · 20 min".
 * Does not require the word "create" / "quiz".
 */
export function parseQuizSizeFollowUp(text) {
  const t = String(text || "").toLowerCase().trim();
  if (!t) return null;
  if (/\bcreate\b/.test(t) && /\bquiz\b/.test(t)) return null; // full create handled elsewhere

  const countMatch =
    t.match(/\b(\d+)\s*(?:medium|easy|hard)?\s*questions?\b/) ||
    t.match(/^(\d+)\s*q\b/) ||
    t.match(/\b(\d+)\s*qs?\b/);
  const minutesMatch =
    t.match(/\b(\d+)\s*[-–—.]?\s*min(?:ute)?s?\b/) ||
    t.match(/\b(\d+)\s*m\b/);
  if (!countMatch || !minutesMatch) return null;

  const difficultyMatch = t.match(/\b(easy|medium|hard)\b/);
  return {
    numQuestions: Math.min(100, Math.max(1, Number(countMatch[1]))),
    timeLimit: Math.min(180, Math.max(1, Number(minutesMatch[1]))),
    difficulty: difficultyMatch?.[1] || "medium",
  };
}

export function buildLocalQuizDraftFromParts({
  catalogue,
  numQuestions = 20,
  timeLimit = 30,
  difficulty = "medium",
  privateBank = false,
}) {
  const cat = String(catalogue || "histology").toLowerCase();
  const name = cat[0].toUpperCase() + cat.slice(1);
  const bankSource = privateBank ? "private" : "microlab";
  // Microlab bank ignores difficulty/level.
  const difficultyFilter = bankSource === "microlab" ? "" : difficulty;
  const draft = {
    ...emptyQuizDraft(),
    title: `${name} Quiz`,
    time_limit_minutes: timeLimit,
    rules_html: DEFAULT_QUIZ_RULES_HTML,
    bank_preference: bankSource,
    awaiting_confirmation: true,
    topic_rules: [
      {
        professor_topic_id: "",
        bank_source: bankSource,
        catalogue: bankSource === "microlab" ? cat : "",
        quiz_title_ids: [],
        topic_label: name,
        num_questions: numQuestions,
        difficulty_filter: difficultyFilter,
      },
    ],
  };
  const confirmation = {
    title: draft.title,
    time_limit_minutes: timeLimit,
    total_questions: numQuestions,
    topic_rules: [
      {
        source: bankSource,
        catalogue: bankSource === "microlab" ? cat : null,
        topic_label: name,
        subtitles: ["All"],
        difficulty: bankSource === "microlab" ? "any" : difficulty,
        num_questions: numQuestions,
      },
    ],
    instructions_preview: String(DEFAULT_QUIZ_RULES_HTML)
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 240),
  };
  return {
    reply: `Here’s a draft ${name} quiz (${numQuestions} questions, ${timeLimit} min). Review it below — confirm to open the Create Quiz form with these settings, or pick another option.`,
    options: [
      { id: "create-quiz", label: "Start over", prompt: "Create a quiz" },
      {
        id: "preset-alt",
        label: "10 questions · 20 min",
        prompt: `Create a ${cat} quiz using 10 questions from Microlab's public bank, with a 20-minute time limit.`,
      },
    ],
    draft,
    confirmation,
  };
}

const QUIZ_CREATE_VERBS =
  /\b(make|maka|mak|mae|create|creat|build|start|prepare|set\s*up)\b/;

/** Fix common free-typed quiz typos before topic / catalogue parsing. */
export function normalizeQuizAskText(text) {
  return String(text || "")
    .replace(/\b(maka|mak|mae)\b/gi, "make")
    .replace(/\b(creat)\b/gi, "create")
    .replace(/\b(oin|ont|abotu|bout)\b/gi, (m) => {
      const x = String(m).toLowerCase();
      if (x === "abotu" || x === "bout") return "about";
      return "on";
    })
    .replace(/\b(tusse|tisse|tissu|tisuue|tisssue)\b/gi, "tissue")
    .replace(/\b(conective|conective|conenctive)\b/gi, "connective")
    .replace(/\b(quizz)\b/gi, "quiz")
    .replace(/\s+/g, " ")
    .trim();
}

/** Bare “create/make a quiz” with no topic or catalogue yet. */
export function isBareQuizCreateAsk(text) {
  const t = normalizeQuizAskText(text).toLowerCase();
  if (!t || !/\bquiz\b/.test(t) || !QUIZ_CREATE_VERBS.test(t)) return false;
  if (/\b(histology|embryology|anatomy|private)\b/.test(t)) return false;
  if (/\b(about|on|for|covering|regarding|from|using)\b/.test(t)) return false;
  if (/\d+\s*(?:medium|easy|hard)?\s*questions?\b/.test(t)) return false;
  if (/\d+\s*[-–—.]?\s*min(?:ute)?s?\b/.test(t)) return false;
  return true;
}

/** Free topic from “make a quiz on connective tissue” (typo-tolerant). */
export function extractFreeQuizTopic(text) {
  const raw = normalizeQuizAskText(text);
  if (!/\bquiz\b/i.test(raw) || !QUIZ_CREATE_VERBS.test(raw)) return null;
  const m =
    raw.match(
      /(?:make|maka|mak|mae|create|creat|build|start|prepare|set\s*up)\s+(?:a\s+|an\s+)?quiz\s+(?:about|abotu|on|oin|ont|for|covering|regarding)\s+(.+)/i
    ) ||
    raw.match(/quiz\s+(?:about|abotu|on|oin|ont|for|covering|regarding)\s+(.+)/i);
  if (!m?.[1]) return null;
  return (
    m[1]
      .replace(/\s+with\s+\d+\s*(?:medium|easy|hard)?\s*questions?.*/i, "")
      .replace(/\s+using\b.*/i, "")
      .replace(/\s+from\b.*/i, "")
      .replace(/[.?!]+$/, "")
      .trim() || null
  );
}

/** Map free medical topics onto Microlab catalogues when possible. */
export function inferCatalogueFromTopic(topicOrText) {
  const t = normalizeQuizAskText(topicOrText).toLowerCase();
  if (!t.trim()) return null;
  if (/\b(histology|embryology|anatomy)\b/.test(t)) {
    return t.match(/\b(histology|embryology|anatomy)\b/)[1];
  }
  if (/\b(embryo|gastrulat|fetal|foetal|placenta|blastocyst|neurulation)\b/.test(t)) {
    return "embryology";
  }
  if (
    /\b(skeleton|joint|muscle system|gross anatomy|organ system|limb|thorax|abdomen|cranium)\b/.test(
      t
    )
  ) {
    return "anatomy";
  }
  if (
    /\b(connective|tissue|epithel|cartilage|histolog|fibroblast|adipose|collagen|basement membrane|glandular|heart|eye|retina|cornea|myocard|vessel|blood)\b/.test(
      t
    )
  ) {
    return "histology";
  }
  return null;
}

function bareQuizCreateOptions() {
  return [
    { id: "cat:histology", label: "Histology (Microlab)", prompt: "cat:histology" },
    { id: "cat:embryology", label: "Embryology (Microlab)", prompt: "cat:embryology" },
    { id: "cat:anatomy", label: "Anatomy (Microlab)", prompt: "cat:anatomy" },
    {
      id: "bank:private",
      label: "My private question bank",
      prompt: "Create a quiz using my private question bank.",
    },
  ];
}

function quizSizeOptionsForTopic(catalogue, topicLabel) {
  const cat = catalogue || "histology";
  const about = topicLabel ? ` about ${topicLabel}` : "";
  return [
    {
      id: "preset-10-20",
      label: "10 questions · 20 min",
      prompt: `Create a ${cat} quiz${about} using 10 questions from Microlab's public bank, with a 20-minute time limit.`,
    },
    {
      id: "preset-20-30",
      label: "20 questions · 30 min",
      prompt: `Create a ${cat} quiz${about} using 20 questions from Microlab's public bank, with a 30-minute time limit.`,
    },
    {
      id: "preset-30-45",
      label: "30 questions · 45 min",
      prompt: `Create a ${cat} quiz${about} using 30 questions from Microlab's public bank, with a 45-minute time limit.`,
    },
    { id: "create-quiz", label: "Pick another catalogue", prompt: "Create a quiz" },
  ];
}

/**
 * Parse a complete “create a … quiz using N …” instruction into a local draft
 * so the chat stays useful when Quiz Copilot is offline.
 */
export function parseLocalQuizCreatePrompt(text) {
  const raw = String(text || "").trim();
  const t = raw.toLowerCase();
  if (!/\bquiz\b/.test(t) || !QUIZ_CREATE_VERBS.test(t)) return null;

  const freeTopic = extractFreeQuizTopic(raw);
  const catalogue =
    t.match(/\b(histology|embryology|anatomy)\b/)?.[1] ||
    inferCatalogueFromTopic(freeTopic || t);
  if (!catalogue) return null;

  const countMatch =
    t.match(/\b(\d+)\s*(?:medium|easy|hard)?\s*questions?\b/) ||
    t.match(/\busing\s+(\d+)\b/);
  const minutesMatch = t.match(/\b(\d+)\s*[- ]?\s*min(?:ute)?s?\b/);
  const difficultyMatch = t.match(/\b(easy|medium|hard)\b/);
  const privateBank = /private/.test(t);

  // Topic-only asks (no count/time yet) are handled by getLocalAssistantFallback.
  if (!countMatch && !minutesMatch && freeTopic) return null;

  return buildLocalQuizDraftFromParts({
    catalogue,
    numQuestions: Math.min(100, Math.max(1, Number(countMatch?.[1] || 20))),
    timeLimit: Math.min(180, Math.max(5, Number(minutesMatch?.[1] || 30))),
    difficulty: difficultyMatch?.[1] || "medium",
    privateBank,
  });
}

/**
 * Local replies when the quiz-copilot API is unavailable or for coming-soon actions.
 * Pass `draft` so short “N questions M min” follow-ups can finish a seeded topic.
 */
export function getLocalAssistantFallback(text, { draft } = {}) {
  const raw = normalizeQuizAskText(text);
  const t = raw.toLowerCase();

  // Only intercept bare “make a presentation” (no topic). Topic requests go to MCP.
  if (
    /^(make|create|creat|build)\s+(a\s+)?[a-z]*pres[a-z]*t[a-z]*ions?\.?$/.test(t) ||
    t === "presentation" ||
    t === "oresenation"
  ) {
    return {
      reply:
        "I can create a presentation with slides and related 3D suggestions. Tell me the topic and how many slides — for example: “Make a presentation about connective tissues with 6 slides”.",
      options: [
        {
          id: "sample-connective",
          label: "Connective tissues · 6 slides",
          prompt: "Make a presentation about connective tissues with 6 slides",
        },
        {
          id: "nav:presentations",
          label: "Open Presentations",
          prompt: "__navigate__:/presentationTitles",
        },
        { id: "guide:presentations", label: "Explain Presentations", prompt: "What is Presentations?" },
      ],
    };
  }

  if (isBareQuizCreateAsk(raw) || t === "create quiz" || t === "make quiz") {
    return {
      reply: "What topic would you like your quiz to cover?",
      options: bareQuizCreateOptions(),
    };
  }

  // Related free-topic asks: “make a quiz on connective tissue”
  const freeTopic = extractFreeQuizTopic(raw);
  if (freeTopic && QUIZ_CREATE_VERBS.test(t) && /\bquiz\b/.test(t)) {
    const inferred = inferCatalogueFromTopic(freeTopic) || inferCatalogueFromTopic(t);
    if (inferred) {
      const name = inferred[0].toUpperCase() + inferred.slice(1);
      const topicName =
        freeTopic.charAt(0).toUpperCase() + freeTopic.slice(1).replace(/\s+/g, " ");
      return {
        reply: `“${topicName}” fits best under **${name}** in Microlab’s public bank. How many questions and what time limit? Pick a preset or type e.g. “10 questions 20 min”.`,
        options: quizSizeOptionsForTopic(inferred, topicName),
        draft: {
          ...emptyQuizDraft(),
          title: `${topicName} Quiz`,
          bank_preference: "microlab",
          awaiting_confirmation: false,
          topic_rules: [
            {
              professor_topic_id: "",
              bank_source: "microlab",
              catalogue: inferred,
              quiz_title_ids: [],
              topic_label: topicName,
              num_questions: 10,
              difficulty_filter: "",
            },
          ],
        },
      };
    }

    return {
      reply: `I can help with a quiz on **${freeTopic}**. Which Microlab catalogue should I use?`,
      options: [
        {
          id: "cat:histology",
          label: "Histology (Microlab)",
          prompt: `Create a histology quiz about ${freeTopic} using 20 questions from Microlab's public bank, with a 30-minute time limit.`,
        },
        {
          id: "cat:embryology",
          label: "Embryology (Microlab)",
          prompt: `Create an embryology quiz about ${freeTopic} using 20 questions from Microlab's public bank, with a 30-minute time limit.`,
        },
        {
          id: "cat:anatomy",
          label: "Anatomy (Microlab)",
          prompt: `Create an anatomy quiz about ${freeTopic} using 20 questions from Microlab's public bank, with a 30-minute time limit.`,
        },
        {
          id: "bank:private",
          label: "My private question bank",
          prompt: "Create a quiz using my private question bank.",
        },
      ],
    };
  }

  const parsedCreate = parseLocalQuizCreatePrompt(raw);
  if (parsedCreate) return parsedCreate;

  const sizeFollowUp = parseQuizSizeFollowUp(raw);
  if (sizeFollowUp) {
    const rule = Array.isArray(draft?.topic_rules) ? draft.topic_rules[0] : null;
    const catalogue = String(rule?.catalogue || rule?.topic_label || "")
      .toLowerCase()
      .trim();
    if (/^(histology|embryology|anatomy)$/.test(catalogue)) {
      return buildLocalQuizDraftFromParts({
        catalogue,
        numQuestions: sizeFollowUp.numQuestions,
        timeLimit: sizeFollowUp.timeLimit,
        difficulty: sizeFollowUp.difficulty || rule?.difficulty_filter || "medium",
        privateBank:
          draft?.bank_preference === "private" || rule?.bank_source === "private",
      });
    }
  }

  const cat = raw.match(/^cat:(histology|embryology|anatomy)$/i);
  if (cat) {
    const catalogue = cat[1].toLowerCase();
    const name = catalogue[0].toUpperCase() + catalogue.slice(1);
    return {
      reply: `Great — I’ll prepare a ${name} quiz from Microlab’s public bank. How many questions and what time limit? You can type e.g. “3 questions 10 min” or pick a preset.`,
      options: [
        {
          id: "preset-10-20",
          label: "10 questions · 20 min",
          prompt: `Create a ${catalogue} quiz using 10 questions from Microlab's public bank, with a 20-minute time limit.`,
        },
        {
          id: "preset-20-30",
          label: "20 questions · 30 min",
          prompt: `Create a ${catalogue} quiz using 20 questions from Microlab's public bank, with a 30-minute time limit.`,
        },
        {
          id: "preset-30-45",
          label: "30 questions · 45 min",
          prompt: `Create a ${catalogue} quiz using 30 questions from Microlab's public bank, with a 45-minute time limit.`,
        },
      ],
      // Seed draft so a short “N questions M min” follow-up can complete the quiz.
      draft: {
        ...emptyQuizDraft(),
        title: `${name} Quiz`,
        bank_preference: "microlab",
        awaiting_confirmation: false,
        topic_rules: [
          {
            professor_topic_id: "",
            bank_source: "microlab",
            catalogue,
            quiz_title_ids: [],
            topic_label: name,
            num_questions: 10,
            difficulty_filter: "",
          },
        ],
      },
    };
  }

  if (/private question bank/.test(t)) {
    return {
      reply:
        "Got it — we’ll use your private question bank. Which topic should I pull questions from? You can also open Course Catalog to organize courses and titles first.",
      options: [
        { id: "create-quiz", label: "Start over", prompt: "Create a quiz" },
        { id: "ask-quizzes", label: "Help with my question bank", prompt: "Help me with my quizzes and question bank" },
      ],
    };
  }

  // Results/analysis intents are handled live (copilot or getQuizResultsCall) — no create-quiz stub.

  if (/help me with my quizzes|ask about my quizzes|question bank/.test(t)) {
    return {
      reply:
        "I can help with quiz setup, question banks, and results for your authorized classes. What would you like to do?",
      options: [
        { id: "create-quiz", label: "Create a quiz", prompt: "Create a quiz" },
        { id: "check-quiz", label: "Check quiz results", prompt: "Show the results of students in my classes" },
        {
          id: "histology-quiz",
          label: "Quick histology quiz",
          prompt:
            "Create a histology quiz using 20 questions from Microlab's public bank, with a 30-minute time limit.",
        },
      ],
    };
  }

  return null;
}
